import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import { 
    BarChart3, 
    TrendingUp, 
    DollarSign, 
    Calendar as CalendarIcon,
    Download,
    Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [paymentFilter, setPaymentFilter] = useState('all');

    useEffect(() => {
        fetchReport();
    }, []);

    const fetchReport = async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/api/reports/sales`;
            const params = new URLSearchParams();
            
            if (startDate) params.append('start_date', startDate.toISOString());
            if (endDate) params.append('end_date', endDate.toISOString());
            if (paymentFilter !== 'all') params.append('payment_method', paymentFilter);
            
            if (params.toString()) url += `?${params.toString()}`;
            
            const response = await axios.get(url);
            setReportData(response.data);
        } catch (error) {
            toast.error('Erro ao carregar relatório');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('pt-BR');
    };

    const dailyChartData = reportData?.by_day 
        ? Object.entries(reportData.by_day)
            .sort(([a], [b]) => a.localeCompare(b))
            .slice(-14)
            .map(([date, data]) => ({
                date: date.slice(5),
                total: data.total,
                count: data.count
            }))
        : [];

    const paymentChartData = reportData?.by_payment_method
        ? Object.entries(reportData.by_payment_method).map(([method, data]) => ({
            method: method.charAt(0).toUpperCase() + method.slice(1),
            total: data.total,
            count: data.count
        }))
        : [];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in-up" data-testid="reports-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-heading font-bold text-white">Relatórios</h1>
                    <p className="text-gray-400 mt-1">Análise financeira completa</p>
                </div>
            </div>

            {/* Filters */}
            <Card className="bg-[#121212] border-[#262626]">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-400 text-sm">Filtros:</span>
                        </div>
                        
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="border-[#262626] text-gray-400" data-testid="start-date-btn">
                                    <CalendarIcon className="w-4 h-4 mr-2" />
                                    {startDate ? format(startDate, 'dd/MM/yyyy') : 'Data Início'}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[#121212] border-[#262626]">
                                <Calendar
                                    mode="single"
                                    selected={startDate}
                                    onSelect={setStartDate}
                                    locale={ptBR}
                                    className="bg-[#121212]"
                                />
                            </PopoverContent>
                        </Popover>

                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="border-[#262626] text-gray-400" data-testid="end-date-btn">
                                    <CalendarIcon className="w-4 h-4 mr-2" />
                                    {endDate ? format(endDate, 'dd/MM/yyyy') : 'Data Fim'}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[#121212] border-[#262626]">
                                <Calendar
                                    mode="single"
                                    selected={endDate}
                                    onSelect={setEndDate}
                                    locale={ptBR}
                                    className="bg-[#121212]"
                                />
                            </PopoverContent>
                        </Popover>

                        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                            <SelectTrigger className="w-40 bg-[#0a0a0a] border-[#262626] text-white" data-testid="payment-filter">
                                <SelectValue placeholder="Pagamento" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-[#262626]">
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="dinheiro">Dinheiro</SelectItem>
                                <SelectItem value="cartao">Cartão</SelectItem>
                                <SelectItem value="pix">PIX</SelectItem>
                            </SelectContent>
                        </Select>

                        <Button 
                            className="bg-rr-yellow-500 text-black hover:bg-rr-yellow-400"
                            onClick={fetchReport}
                            data-testid="apply-filters-btn"
                        >
                            Aplicar
                        </Button>

                        {(startDate || endDate || paymentFilter !== 'all') && (
                            <Button
                                variant="ghost"
                                className="text-gray-400"
                                onClick={() => {
                                    setStartDate(null);
                                    setEndDate(null);
                                    setPaymentFilter('all');
                                    setTimeout(fetchReport, 0);
                                }}
                            >
                                Limpar
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-[#121212] border-[#262626]" data-testid="total-revenue-card">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Receita Total</p>
                                <p className="text-3xl font-heading font-bold text-rr-yellow-500 mt-2">
                                    {formatCurrency(reportData?.summary?.total_revenue || 0)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-rr-yellow-500/10 rounded-md flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-rr-yellow-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-[#121212] border-[#262626]" data-testid="total-profit-card">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Lucro Total</p>
                                <p className="text-3xl font-heading font-bold text-emerald-500 mt-2">
                                    {formatCurrency(reportData?.summary?.total_profit || 0)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-emerald-500/10 rounded-md flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-emerald-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-[#121212] border-[#262626]" data-testid="total-sales-card">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Total Vendas</p>
                                <p className="text-3xl font-heading font-bold text-white mt-2">
                                    {reportData?.summary?.total_count || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-white/5 rounded-md flex items-center justify-center">
                                <BarChart3 className="w-6 h-6 text-gray-400" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-[#121212] border-[#262626]" data-testid="avg-ticket-card">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Ticket Médio</p>
                                <p className="text-3xl font-heading font-bold text-blue-500 mt-2">
                                    {formatCurrency(reportData?.summary?.average_ticket || 0)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-500/10 rounded-md flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-blue-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Daily Sales Chart */}
                <Card className="bg-[#121212] border-[#262626]" data-testid="daily-chart">
                    <CardHeader>
                        <CardTitle className="text-white font-heading">Vendas por Dia</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-80 w-full min-h-[320px]">
                            <ResponsiveContainer width="100%" height="100%" minWidth={300}>
                                <LineChart data={dailyChartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                                    <XAxis dataKey="date" stroke="#888" />
                                    <YAxis stroke="#888" tickFormatter={(v) => `R$${v}`} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#121212',
                                            border: '1px solid #262626',
                                            borderRadius: '6px'
                                        }}
                                        formatter={(value) => [formatCurrency(value), 'Receita']}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="total" 
                                        stroke="#D4A84B" 
                                        strokeWidth={2}
                                        dot={{ fill: '#D4A84B', strokeWidth: 2 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Payment Methods Chart */}
                <Card className="bg-[#121212] border-[#262626]" data-testid="payment-chart">
                    <CardHeader>
                        <CardTitle className="text-white font-heading">Por Forma de Pagamento</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-80 w-full min-h-[320px]">
                            <ResponsiveContainer width="100%" height="100%" minWidth={300}>
                                <BarChart data={paymentChartData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                                    <XAxis type="number" stroke="#888" tickFormatter={(v) => `R$${v}`} />
                                    <YAxis type="category" dataKey="method" stroke="#888" width={80} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#121212',
                                            border: '1px solid #262626',
                                            borderRadius: '6px'
                                        }}
                                        formatter={(value) => [formatCurrency(value), 'Total']}
                                    />
                                    <Bar dataKey="total" fill="#D4A84B" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Recent Sales Table */}
            <Card className="bg-[#121212] border-[#262626]">
                <CardHeader>
                    <CardTitle className="text-white font-heading">Últimas Vendas</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow className="border-[#262626] hover:bg-transparent">
                                <TableHead className="text-gray-400">ID</TableHead>
                                <TableHead className="text-gray-400">Data</TableHead>
                                <TableHead className="text-gray-400">Itens</TableHead>
                                <TableHead className="text-gray-400">Pagamento</TableHead>
                                <TableHead className="text-gray-400">Vendedor</TableHead>
                                <TableHead className="text-gray-400 text-right">Total</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(reportData?.sales || []).slice(0, 10).map(sale => (
                                <TableRow key={sale.id} className="border-[#262626] hover:bg-white/5">
                                    <TableCell className="text-white font-mono text-sm">
                                        #{sale.id.slice(0, 8)}
                                    </TableCell>
                                    <TableCell className="text-gray-400">
                                        {formatDate(sale.created_at)}
                                    </TableCell>
                                    <TableCell className="text-gray-400">
                                        {sale.items.length} itens
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="border-[#333] text-gray-400 capitalize">
                                            {sale.payment_method}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-gray-400">
                                        {sale.seller_name}
                                    </TableCell>
                                    <TableCell className="text-right text-rr-yellow-500 font-bold">
                                        {formatCurrency(sale.total)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {(!reportData?.sales || reportData.sales.length === 0) && (
                        <div className="text-center py-12 text-gray-500">
                            Nenhuma venda encontrada no período
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ReportsPage;
